/**
 * STON.fi DEX Service
 */
import axios from 'axios';
import { getApiUrl } from '../utils/api';

const API_URL = getApiUrl();

const stonfiApi = axios.create({
  baseURL: `${API_URL}/api/stonfi`,
  headers: {
    'Content-Type': 'application/json',
  },
});

export const stonfiService = {
  /**
   * Get all available pools
   * @returns {Promise<Array>} List of pools
   */
  async getPools() {
    try {
      const response = await stonfiApi.get('/pools');
      return response.data;
    } catch (error) {
      console.error('Error getting pools:', error);
      throw error;
    }
  },

  /**
   * Get specific pool info
   * @param {string} poolId - Pool ID
   * @returns {Promise<Object>} Pool data
   */
  async getPool(poolId) {
    try {
      const response = await stonfiApi.get(`/pools/${poolId}`);
      return response.data;
    } catch (error) {
      console.error('Error getting pool:', error);
      throw error;
    }
  },

  /**
   * Estimate swap output
   * @param {string} tokenIn - Input token address
   * @param {string} tokenOut - Output token address
   * @param {string} amountIn - Amount to swap
   * @returns {Promise<Object>} Swap estimate
   */
  async estimateSwap(tokenIn, tokenOut, amountIn) {
    try {
      const response = await stonfiApi.post('/swap/estimate', {
        token_in: tokenIn,
        token_out: tokenOut,
        amount_in: amountIn,
      });
      return response.data;
    } catch (error) {
      console.error('Error estimating swap:', error);
      throw error;
    }
  },

  /**
   * Execute swap
   * @param {Object} params - Swap parameters
   * @returns {Promise<Object>} Transaction result
   */
  async executeSwap(params) {
    try {
      const response = await stonfiApi.post('/swap/execute', {
        wallet_address: params.walletAddress,
        token_in: params.tokenIn,
        token_out: params.tokenOut,
        amount_in: params.amountIn,
        min_amount_out: params.minAmountOut,
        slippage_tolerance: params.slippageTolerance || 0.01,
      });
      return response.data;
    } catch (error) {
      console.error('Error executing swap:', error);
      throw error;
    }
  },

  /**
   * Get token price
   * @param {string} tokenAddress - Token address
   * @returns {Promise<Object>} Price data
   */
  async getTokenPrice(tokenAddress) {
    try {
      const response = await stonfiApi.get(`/tokens/${tokenAddress}/price`);
      return response.data;
    } catch (error) {
      console.error('Error getting token price:', error);
      throw error;
    }
  },

  /**
   * Get user liquidity positions
   * @param {string} walletAddress - Wallet address
   * @returns {Promise<Array>} Liquidity positions
   */
  async getUserLiquidity(walletAddress) {
    try {
      const response = await stonfiApi.get(`/liquidity/${walletAddress}`);
      return response.data;
    } catch (error) {
      console.error('Error getting liquidity:', error);
      throw error;
    }
  },

  /**
   * Health check
   * @returns {Promise<Object>} Health status
   */
  async healthCheck() {
    try {
      const response = await stonfiApi.get('/health');
      return response.data;
    } catch (error) {
      console.error('Error checking health:', error);
      throw error;
    }
  },
};

export default stonfiService;
